import React, { useEffect, useState } from 'react';
import axios from '../config/api';

type Settings = {
  enabled: boolean;
  percent: number;
  max_percent: number;
  commission_percent: number;
  commission_override_percent: number | null;
  commission_charged: boolean;
};

export default function DineInDiscountCard({
  cafeId,
  cafeName,
  isSupportAdmin,
  showToast,
}: {
  cafeId: string;
  cafeName?: string;
  isSupportAdmin: boolean;
  showToast: (message: string, type?: 'success' | 'error' | 'info') => void;
}) {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [percent, setPercent] = useState('10');
  const [saving, setSaving] = useState(false);
  const [overrideInput, setOverrideInput] = useState('');
  const [savingCommission, setSavingCommission] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get<Settings>(`/api/cafes/${cafeId}/dine-in`)
      .then((res) => {
        if (cancelled) return;
        setSettings(res.data);
        setEnabled(Boolean(res.data.enabled));
        setPercent(String(res.data.percent ?? 0));
        setOverrideInput(
          res.data.commission_override_percent == null
            ? ''
            : String(res.data.commission_override_percent)
        );
      })
      .catch((err) => {
        if (!cancelled) {
          showToast(err?.response?.data?.error || 'Could not load dine-in settings', 'error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [cafeId, showToast]);

  const save = async () => {
    const value = Number(percent);
    if (!Number.isFinite(value) || value < 0 || value > 100) {
      showToast('Discount must be between 0 and 100', 'error');
      return;
    }
    if (value > 50) {
      showToast('Dine-in discount cannot exceed 50%', 'error');
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.patch<Settings>(`/api/cafes/${cafeId}/dine-in`, {
        enabled,
        percent: value,
      });
      setSettings(data);
      setEnabled(Boolean(data.enabled));
      setPercent(String(data.percent));
      showToast('Dine-in discount saved', 'success');
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Could not save dine-in discount', 'error');
    } finally {
      setSaving(false);
    }
  };

  const saveCommission = async (clear: boolean) => {
    const body = clear ? { percent: null } : { percent: Number(overrideInput) };
    if (!clear) {
      const value = Number(overrideInput);
      if (!Number.isFinite(value) || value < 0 || value > 100) {
        showToast('Commission percent must be between 0 and 100', 'error');
        return;
      }
    }
    setSavingCommission(true);
    try {
      const { data } = await axios.patch<Settings>(`/api/cafes/${cafeId}/dine-in-commission`, body);
      setSettings(data);
      setOverrideInput(
        data.commission_override_percent == null ? '' : String(data.commission_override_percent)
      );
      showToast(clear ? 'Using the default commission' : 'Commission override saved', 'success');
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Could not update commission', 'error');
    } finally {
      setSavingCommission(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 border border-amber-100 mb-8">
      <h3 className="text-base font-bold text-amber-800 mb-1">Dine-in discount</h3>
      <p className="text-sm text-gray-600 mb-4">
        {cafeName || 'This café'}: turn the dine-in discount on or off. Customers place a normal
        pickup order. The café scans that order, and the server applies this percent.
      </p>

      <div className="flex flex-wrap items-end gap-4">
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          onClick={() => setEnabled((on) => !on)}
          className={`relative h-8 w-14 rounded-full transition ${enabled ? 'bg-amber-700' : 'bg-gray-300'}`}
        >
          <span
            className={`absolute top-1 h-6 w-6 rounded-full bg-white transition ${
              enabled ? 'left-7' : 'left-1'
            }`}
          />
          <span className="sr-only">{enabled ? 'Discount on' : 'Discount off'}</span>
        </button>
        <label className="text-sm text-gray-700">
          Percent off
          <input
            type="number"
            min={0}
            max={50}
            step="0.5"
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
            className="mt-1 block w-28 rounded border border-gray-300 px-2 py-1.5"
          />
        </label>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="rounded-lg bg-amber-800 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <p className="text-xs text-gray-500 pb-2">0–50. Higher than 50 is rejected.</p>
      </div>

      <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-700">
        <p className="font-semibold text-gray-900">Platform commission</p>
        <p className="mt-1">
          {settings ? `${settings.commission_percent}%` : '…'} of the drink total after the dine-in
          discount. Recorded on each dine-in order. Not collected until card payments are connected.
        </p>
        {isSupportAdmin ? (
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <label className="text-xs text-gray-600">
              Café override (blank uses the server default)
              <input
                type="number"
                min={0}
                max={100}
                step="0.5"
                value={overrideInput}
                onChange={(e) => setOverrideInput(e.target.value)}
                className="mt-1 block w-28 rounded border border-gray-300 px-2 py-1.5 text-sm"
              />
            </label>
            <button
              type="button"
              disabled={savingCommission}
              onClick={() => saveCommission(false)}
              className="rounded border border-amber-800 px-3 py-1.5 text-xs font-semibold text-amber-900"
            >
              Save override
            </button>
            <button
              type="button"
              disabled={savingCommission}
              onClick={() => saveCommission(true)}
              className="rounded px-3 py-1.5 text-xs font-semibold text-gray-600 underline"
            >
              Use default
            </button>
          </div>
        ) : (
          <p className="mt-2 text-xs text-gray-500">Only a superuser can change this rate.</p>
        )}
      </div>
    </div>
  );
}
